import { del } from 'idb-keyval';
import { TimeElapsed } from '../types';

// Completely purge all local browser storage (localStorage & IndexedDB)
export async function clearAllLocalStorageAndIndexedDB(): Promise<void> {
  try {
    if (typeof window !== 'undefined') {
      // Clear all possible legacy keys from localStorage
      const keysToRemove = [
        'pareja_settings_v1',
        'pareja_memories_v1',
        'pareja_milestones_v1',
        'pareja_daily_letters_v1',
        'pareja_special_events_v1',
        'pareja_settings_v2',
        'pareja_memories_v2',
        'pareja_milestones_v2',
        'pareja_daily_letters_v2',
        'pareja_special_events_v2',
        'pareja_v2_cleaned',
        'pareja_data_purged_v2',
      ];
      for (const k of keysToRemove) {
        localStorage.removeItem(k);
      }

      // Clear all legacy keys from IndexedDB
      await del('pareja_memories_v1');
      await del('pareja_milestones_v1');
      await del('pareja_daily_letters_v1');
      await del('pareja_special_events_v1');
      await del('pareja_memories_v2');
      await del('pareja_milestones_v2');
      await del('pareja_daily_letters_v2');
    }
  } catch (err) {
    console.warn('Could not clear local caches:', err);
  }
}

/**
 * Safely parses a start date string into a local Date object without UTC drift.
 * Supports:
 * - YYYY-MM-DD
 * - YYYY-MM-DDTHH:mm:ss
 * - YYYY-MM-DD HH:mm:ss
 * - Full ISO 8601 strings
 */
export function parseStartDate(startDateStr: string): Date | null {
  if (!startDateStr || typeof startDateStr !== 'string') return null;
  const cleanStr = startDateStr.trim();
  if (!cleanStr) return null;

  // Regex to extract YYYY, MM, DD, and optional HH, mm, ss
  const match = cleanStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/);
  if (match) {
    const year = parseInt(match[1], 10);
    const monthIndex = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const hour = match[4] ? parseInt(match[4], 10) : 0;
    const minute = match[5] ? parseInt(match[5], 10) : 0;
    const second = match[6] ? parseInt(match[6], 10) : 0;

    const d = new Date(year, monthIndex, day, hour, minute, second, 0);
    if (!isNaN(d.getTime())) {
      return d;
    }
  }

  const fallback = new Date(cleanStr);
  return isNaN(fallback.getTime()) ? null : fallback;
}

/**
 * Returns a new Date corresponding to advancing `start` by `years` and `months`,
 * correctly clamping the day to the maximum valid days of the target calendar month
 * (taking into account leap years and months with 28, 29, 30, or 31 days).
 */
export function addCalendarYearsAndMonths(start: Date, years: number, months: number): Date {
  const totalMonths = start.getMonth() + months;
  const targetYear = start.getFullYear() + years + Math.floor(totalMonths / 12);
  const targetMonth = ((totalMonths % 12) + 12) % 12;

  // Days in targetMonth of targetYear: (day 0 of targetMonth + 1 gives the last day of targetMonth)
  const maxDaysInMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
  const targetDay = Math.min(start.getDate(), maxDaysInMonth);

  return new Date(
    targetYear,
    targetMonth,
    targetDay,
    start.getHours(),
    start.getMinutes(),
    start.getSeconds(),
    start.getMilliseconds()
  );
}

// Calculate elapsed time accurately between startDate and now based on real calendar math
export function calculateTimeElapsed(startDateStr: string, referenceDate?: Date): TimeElapsed {
  const emptyResult: TimeElapsed = {
    years: 0,
    months: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    totalDays: 0,
  };

  const start = parseStartDate(startDateStr);
  if (!start) return emptyResult;

  const now = referenceDate ? new Date(referenceDate.getTime()) : new Date();

  // If start is in the future or invalid, return all zeroes cleanly
  if (isNaN(start.getTime()) || isNaN(now.getTime()) || start.getTime() > now.getTime()) {
    return emptyResult;
  }

  // 1. Calculate full elapsed calendar years
  let years = now.getFullYear() - start.getFullYear();
  while (years > 0 && addCalendarYearsAndMonths(start, years, 0).getTime() > now.getTime()) {
    years--;
  }

  // 2. Calculate full elapsed calendar months (0 to 11)
  let months = 0;
  while (months < 11 && addCalendarYearsAndMonths(start, years, months + 1).getTime() <= now.getTime()) {
    months++;
  }

  // 3. Anchor date after full elapsed years and months
  const anchor = addCalendarYearsAndMonths(start, years, months);

  // 4. Exact difference in milliseconds between anchor and now
  let diffMs = Math.max(0, now.getTime() - anchor.getTime());

  const MS_PER_SEC = 1000;
  const MS_PER_MIN = 60 * MS_PER_SEC;
  const MS_PER_HOUR = 60 * MS_PER_MIN;
  const MS_PER_DAY = 24 * MS_PER_HOUR;

  const days = Math.floor(diffMs / MS_PER_DAY);
  diffMs -= days * MS_PER_DAY;

  const hours = Math.floor(diffMs / MS_PER_HOUR);
  diffMs -= hours * MS_PER_HOUR;

  const minutes = Math.floor(diffMs / MS_PER_MIN);
  diffMs -= minutes * MS_PER_MIN;

  const seconds = Math.floor(diffMs / MS_PER_SEC);

  const totalDays = Math.floor((now.getTime() - start.getTime()) / MS_PER_DAY);

  return {
    years: Math.max(0, Number.isFinite(years) ? years : 0),
    months: Math.max(0, Number.isFinite(months) ? months : 0),
    days: Math.max(0, Number.isFinite(days) ? days : 0),
    hours: Math.max(0, Number.isFinite(hours) ? hours : 0),
    minutes: Math.max(0, Number.isFinite(minutes) ? minutes : 0),
    seconds: Math.max(0, Number.isFinite(seconds) ? seconds : 0),
    totalDays: Math.max(0, Number.isFinite(totalDays) ? totalDays : 0),
  };
}

// Format date in Spanish (e.g., "14 de Febrero, 2024")
export function formatDateSpanish(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  if (isNaN(date.getTime())) return dateStr;

  return date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// Format date and exact time in Spanish (e.g., "18 de septiembre de 2026, 02:40 PM")
export function formatDateTimeSpanish(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;

  return date.toLocaleString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

